import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';

// ============================================================
// TYPES
// ============================================================
interface AttachmentFile {
  filename: string;
  original_name: string;
  url: string;
}

interface Attestation {
  id: number;
  user_id: number;
  session_id: string;
  manufacturer_name: string;
  ndc_code: string | null;
  product_name: string | null;
  claim_type: string;
  claim_details: Record<string, unknown>;
  conversation_log?: Array<{ role: string; content: string }>;
  attachments?: AttachmentFile[];
  status: string;
  reviewer_id?: number;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  verification_status?: string;
  verification_email?: string;
  verified_at?: string | null;
  email_domain_mismatch?: boolean;
}

interface SelfAttestationsPageProps {
}

// ============================================================
// DESIGN TOKENS
// ============================================================
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  pending_review: { bg: '#FEF3C7', text: '#92400E', label: 'Pending Review' },
  approved: { bg: '#D1FAE5', text: '#065F46', label: 'Approved' },
  rejected: { bg: '#FEE2E2', text: '#991B1B', label: 'Rejected' },
};

const VERIFICATION_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  unverified: { bg: '#FEE2E2', text: '#991B1B', label: 'Unverified' },
  pending: { bg: '#FEF3C7', text: '#92400E', label: 'Verification Pending' },
  verified: { bg: '#D1FAE5', text: '#065F46', label: 'Verified' },
};

// ============================================================
// COMPONENT
// ============================================================
export default function SelfAttestationsPage({}: SelfAttestationsPageProps) {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedAttestation, setSelectedAttestation] = useState<Attestation | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const loadAttestations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '50');
      const response = await fetch(`${API_BASE}/chatbot/attestations?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAttestations(data.attestations || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load attestations:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadAttestations();
  }, [loadAttestations]);

  const loadAttestationDetail = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/chatbot/attestations/${id}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setSelectedAttestation(data);
      setReviewNotes('');
    } catch (error) {
      console.error('Failed to load attestation detail:', error);
    }
  };

  const handleReview = async (status: 'approved' | 'rejected') => {
    if (!selectedAttestation) return;
    setReviewing(true);
    try {
      const response = await fetch(`${API_BASE}/chatbot/attestations/${selectedAttestation.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, reviewer_notes: reviewNotes }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Failed to review' }));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.confidence_updated) {
        alert('Attestation approved and product confidence updated to 100% Made in USA!');
      }
      setSelectedAttestation(null);
      loadAttestations();
    } catch (error) {
      console.error('Failed to review attestation:', error);
      alert(error instanceof Error ? error.message : 'Failed to update attestation. Please try again.');
    } finally {
      setReviewing(false);
    }
  };

  const handleRemoveApproval = async () => {
    if (!selectedAttestation) return;
    if (!window.confirm('Remove approval? This will restore the original programmatic values for this product.')) return;
    setReviewing(true);
    try {
      const response = await fetch(`${API_BASE}/chatbot/attestations/${selectedAttestation.id}/remove-approval`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      alert('Approval removed. Original product values have been restored.');
      setSelectedAttestation(null);
      loadAttestations();
    } catch (error) {
      console.error('Failed to remove approval:', error);
      alert('Failed to remove approval. Please try again.');
    } finally {
      setReviewing(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_COLORS[status] || { bg: '#F3F4F6', text: '#374151', label: status };
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 6,
        background: config.bg,
        color: config.text,
        fontSize: 12,
        fontWeight: 700,
      }}>
        {config.label}
      </span>
    );
  };

  // ============================================================
  // DETAIL VIEW
  // ============================================================
  if (selectedAttestation) {
    const a = selectedAttestation;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => setSelectedAttestation(null)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: String(T.white),
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              color: String(T.textDark),
            }}
          >
            &larr; Back to list
          </button>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: String(T.navy) }}>
            Attestation #{a.id}
          </h2>
          {getStatusBadge(a.status)}
          {a.verification_status && (() => {
            const vc = VERIFICATION_COLORS[a.verification_status] || VERIFICATION_COLORS.unverified;
            return (
              <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 6,
                background: vc.bg,
                color: vc.text,
                fontSize: 12,
                fontWeight: 700,
              }}>
                {vc.label}
              </span>
            );
          })()}
        </div>

        {/* Verification Info Banner */}
        {a.verification_status && a.verification_status !== 'verified' && (
          <div style={{
            padding: 16,
            borderRadius: Number(T.radius),
            background: '#FFFBEB',
            border: '1px solid #FDE68A',
            fontSize: 13,
            lineHeight: 1.6,
            color: '#78350F',
          }}>
            <strong>Email Verification Required:</strong> This attestation was submitted from a non-corporate email.
            The submitter must verify their identity via email before this attestation can be approved.
            {a.verification_email && (
              <div style={{ marginTop: 6 }}>
                Verification email sent to: <strong>{a.verification_email}</strong>
              </div>
            )}
          </div>
        )}

        {a.verification_status === 'verified' && a.verified_at && (
          <div style={{
            padding: 16,
            borderRadius: Number(T.radius),
            background: '#ECFDF5',
            border: '1px solid #BBF7D0',
            fontSize: 13,
            lineHeight: 1.6,
            color: '#065F46',
          }}>
            <strong>Identity Verified</strong> &mdash; Email verified on {new Date(a.verified_at).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
            {a.verification_email && <> via <strong>{a.verification_email}</strong></>}
          </div>
        )}

        {/* Details Card */}
        <div style={{
          background: String(T.white),
          border: `1px solid ${T.border}`,
          borderRadius: Number(T.radius),
          padding: 24,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 16 }}>
            Attestation Details
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px 24px' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manufacturer</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark), marginTop: 4 }}>{a.manufacturer_name || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>NDC Code</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark), marginTop: 4, fontFamily: 'monospace' }}>{a.ndc_code || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Name</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark), marginTop: 4 }}>{a.product_name || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>Claim Type</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark), marginTop: 4 }}>
                {a.claim_type === 'made_in_america' ? 'Made in America' : a.claim_type}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submitted</div>
              <div style={{ fontSize: 14, color: String(T.textDark), marginTop: 4 }}>{formatDate(a.created_at)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reviewed</div>
              <div style={{ fontSize: 14, color: String(T.textDark), marginTop: 4 }}>{formatDate(a.reviewed_at)}</div>
            </div>
          </div>

          {/* Evidence / Claim Details */}
          {a.claim_details && Object.keys(a.claim_details).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: String(T.textLight), textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Evidence & Claim Details
              </div>
              <div style={{
                background: '#F8FAFC',
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: 16,
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                color: String(T.textDark),
              }}>
                {typeof a.claim_details === 'object'
                  ? Object.entries(a.claim_details).map(([key, val]) => (
                    <div key={key} style={{ marginBottom: 4 }}>
                      <strong style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</strong>{' '}
                      {String(val)}
                    </div>
                  ))
                  : JSON.stringify(a.claim_details, null, 2)
                }
              </div>
            </div>
          )}
        </div>

        {/* Attachments */}
        {a.attachments && a.attachments.length > 0 && (
          <div style={{
            background: String(T.white),
            border: `1px solid ${T.border}`,
            borderRadius: Number(T.radius),
            padding: 24,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 16 }}>
              Uploaded Evidence Files ({a.attachments.length})
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {a.attachments.map((file, i) => (
                <a
                  key={i}
                  href={`${API_BASE}${file.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={file.original_name}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    background: '#F8FAFC',
                    color: String(T.navy),
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = '#EEF2FF'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = '#F8FAFC'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {file.original_name}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Conversation Log */}
        {a.conversation_log && a.conversation_log.length > 0 && (
          <div style={{
            background: String(T.white),
            border: `1px solid ${T.border}`,
            borderRadius: Number(T.radius),
            padding: 24,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 16 }}>
              Conversation Log
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 400, overflowY: 'auto' }}>
              {a.conversation_log.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    background: msg.role === 'user' ? String(T.navy) : '#F3F4F6',
                    color: msg.role === 'user' ? '#FFFFFF' : String(T.textDark),
                    fontSize: 13,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, marginBottom: 4, textTransform: 'uppercase' }}>
                      {msg.role === 'user' ? 'Manufacturer' : 'AI Assistant'}
                    </div>
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Actions */}
        {a.status === 'pending_review' && (
          <div style={{
            background: String(T.white),
            border: `1px solid ${T.border}`,
            borderRadius: Number(T.radius),
            padding: 24,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 16 }}>
              Review Decision
            </h3>

            {/* Gate: cannot approve if not verified */}
            {a.verification_status && a.verification_status !== 'verified' && (
              <div style={{
                padding: 12,
                borderRadius: 8,
                background: '#FEF3C7',
                border: '1px solid #FDE68A',
                marginBottom: 16,
                fontSize: 13,
                color: '#92400E',
                fontWeight: 600,
              }}>
                The Approve button is disabled because this attestation has not been verified via email yet.
                Current verification status: {VERIFICATION_COLORS[a.verification_status]?.label || a.verification_status}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: String(T.textDark), display: 'block', marginBottom: 6 }}>
                Reviewer Notes
              </label>
              <textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                placeholder="Add notes about your review decision..."
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: `2px solid ${T.border}`,
                  fontSize: 14,
                  fontFamily: "'DM Sans', -apple-system, sans-serif",
                  minHeight: 80,
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => handleReview('approved')}
                disabled={reviewing || (!!a.email_domain_mismatch && !!a.verification_status && a.verification_status !== 'verified')}
                title={a.email_domain_mismatch && a.verification_status && a.verification_status !== 'verified' ? 'Cannot approve until email verification is complete' : 'Approve this attestation'}
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: (a.email_domain_mismatch && a.verification_status && a.verification_status !== 'verified') ? '#9CA3AF' : '#059669',
                  color: '#FFFFFF',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: (reviewing || (a.email_domain_mismatch && a.verification_status && a.verification_status !== 'verified')) ? 'not-allowed' : 'pointer',
                  opacity: reviewing ? 0.6 : 1,
                }}
              >
                {reviewing ? 'Processing...' : 'Approve'}
              </button>
              <button
                onClick={() => handleReview('rejected')}
                disabled={reviewing}
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#DC2626',
                  color: '#FFFFFF',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: reviewing ? 'not-allowed' : 'pointer',
                  opacity: reviewing ? 0.6 : 1,
                }}
              >
                {reviewing ? 'Processing...' : 'Reject'}
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: String(T.textLight) }}>
              Approving a "Made in America" attestation will update the product's confidence score to 100%.
            </div>
          </div>
        )}

        {/* Previous review info */}
        {a.status !== 'pending_review' && a.reviewer_notes && (
          <div style={{
            background: String(T.white),
            border: `1px solid ${T.border}`,
            borderRadius: Number(T.radius),
            padding: 24,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 8 }}>
              Reviewer Notes
            </h3>
            <p style={{ fontSize: 14, color: String(T.textDark), lineHeight: 1.6 }}>{a.reviewer_notes}</p>
          </div>
        )}

        {/* Remove Approval action for approved attestations */}
        {a.status === 'approved' && (
          <div style={{
            background: String(T.white),
            border: `1px solid ${T.border}`,
            borderRadius: Number(T.radius),
            padding: 24,
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: String(T.navy), marginBottom: 8 }}>
              Admin Actions
            </h3>
            <p style={{ fontSize: 13, color: String(T.textMid), marginBottom: 16, lineHeight: 1.6 }}>
              Removing approval will reset this attestation to pending review and restore the product's
              original programmatic country of origin, confidence score, and Made in America percentage.
            </p>
            <button
              onClick={handleRemoveApproval}
              disabled={reviewing}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                border: '2px solid #DC2626',
                background: '#FFFFFF',
                color: '#DC2626',
                fontSize: 14,
                fontWeight: 700,
                cursor: reviewing ? 'not-allowed' : 'pointer',
                opacity: reviewing ? 0.6 : 1,
              }}
            >
              {reviewing ? 'Processing...' : 'Remove Approval'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // LIST VIEW
  // ============================================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: String(T.navy) }}>Self-Attestations</h2>
        <p style={{ fontSize: 14, color: String(T.textMid), marginTop: 4 }}>
          Review manufacturer self-attestation claims about product country of origin.
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        {['', 'pending_review', 'approved', 'rejected'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: `1px solid ${statusFilter === status ? T.gold : T.border}`,
              background: statusFilter === status ? `${T.gold}15` : String(T.white),
              color: statusFilter === status ? String(T.gold) : String(T.textMid),
              fontSize: 13,
              fontWeight: statusFilter === status ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {status === '' ? 'All' : STATUS_COLORS[status]?.label || status}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: String(T.textLight) }}>
          {total} total
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: String(T.textLight) }}>
          Loading attestations...
        </div>
      )}

      {/* Empty state */}
      {!loading && attestations.length === 0 && (
        <div style={{
          background: String(T.white),
          border: `1px solid ${T.border}`,
          borderRadius: Number(T.radius),
          padding: 40,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: String(T.navy) }}>No attestations found</div>
          <p style={{ fontSize: 13, color: String(T.textMid), marginTop: 8 }}>
            {statusFilter ? `No attestations with status "${STATUS_COLORS[statusFilter]?.label || statusFilter}".` : 'Manufacturers can submit attestations through the AI chatbot.'}
          </p>
        </div>
      )}

      {/* Attestation list */}
      {!loading && attestations.length > 0 && (
        <div style={{
          background: String(T.white),
          border: `1px solid ${T.border}`,
          borderRadius: Number(T.radius),
          overflow: 'hidden',
        }}>
          {/* Desktop: Table header */}
          {!isMobile && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 140px 1fr 120px 110px 100px',
              padding: '12px 20px',
              background: '#F8FAFC',
              borderBottom: `1px solid ${T.border}`,
              fontSize: 11,
              fontWeight: 700,
              color: String(T.textLight),
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              <div>ID</div>
              <div>Manufacturer</div>
              <div>NDC</div>
              <div>Claim</div>
              <div>Status</div>
              <div>Verified</div>
              <div>Date</div>
            </div>
          )}

          {/* Table rows / Mobile cards */}
          {attestations.map(a => (
            isMobile ? (
              <div
                key={a.id}
                onClick={() => loadAttestationDetail(a.id)}
                style={{
                  padding: '14px 16px',
                  borderBottom: `1px solid ${T.border}`,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: String(T.textLight) }}>#{a.id}</span>
                  {getStatusBadge(a.status)}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark) }}>
                  {a.manufacturer_name || '-'}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: String(T.textMid) }}>
                  <span>NDC: {a.ndc_code || '-'}</span>
                  <span>{a.claim_type === 'made_in_america' ? 'Made in America' : a.claim_type}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {(() => {
                    const vs = a.verification_status || 'unverified';
                    const vc = VERIFICATION_COLORS[vs] || VERIFICATION_COLORS.unverified;
                    return (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: 6,
                        background: vc.bg,
                        color: vc.text,
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        {vc.label}
                      </span>
                    );
                  })()}
                  <span style={{ fontSize: 12, color: String(T.textLight) }}>
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                  </span>
                </div>
              </div>
            ) : (
              <div
                key={a.id}
                onClick={() => loadAttestationDetail(a.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 140px 1fr 120px 110px 100px',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${T.border}`,
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                  alignItems: 'center',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#F8FAFC'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: String(T.textLight) }}>#{a.id}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: String(T.textDark), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.manufacturer_name || '-'}
                </div>
                <div style={{ fontSize: 13, fontFamily: 'monospace', color: String(T.textMid) }}>
                  {a.ndc_code || '-'}
                </div>
                <div style={{ fontSize: 13, color: String(T.textMid), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.claim_type === 'made_in_america' ? 'Made in America' : a.claim_type}
                </div>
                <div>{getStatusBadge(a.status)}</div>
                <div>
                  {(() => {
                    const vs = a.verification_status || 'unverified';
                    const vc = VERIFICATION_COLORS[vs] || VERIFICATION_COLORS.unverified;
                    return (
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: 6,
                        background: vc.bg,
                        color: vc.text,
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        {vc.label}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ fontSize: 12, color: String(T.textLight) }}>
                  {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
